import type { CompanyDomain, CreateCompanyDomainRequest } from '@shared/domain/company/companyDomain.types';
import { useApi } from '@shared/services/api';
import { useCallback, useEffect, useState } from 'react';

export interface MutationResult {
  error?: boolean;
  message?: string;
  data?: CompanyDomain | CompanyDomain[] | null;
}

export interface UseCompanyDomainsResult {
  domains: CompanyDomain[];
  loading: boolean;
  fetchDomains: () => Promise<void>;
  createDomain: (data: CreateCompanyDomainRequest) => Promise<MutationResult>;
  deleteDomain: (id: number) => Promise<MutationResult>;
  setPrimary: (id: number) => Promise<MutationResult>;
}

export function useCompanyDomains(companyId?: number | null): UseCompanyDomainsResult {
  const api = useApi();
  const [domains, setDomains] = useState<CompanyDomain[]>([]);
  const [loading, setLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchDomains = useCallback(async () => {
    if (!companyId) {
      setDomains([]);
      return;
    }
    setLoading(true);
    const res: any = await api.get(`company/${companyId}/domains`);
    if (!res.error) {
      setDomains((res.data ?? []) as CompanyDomain[]);
    }
    setLoading(false);
  }, [companyId]); // api intentionally omitted

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const createDomain = useCallback(
    async (data: CreateCompanyDomainRequest): Promise<MutationResult> => {
      if (!companyId) {
        return { error: true, message: 'No company selected' };
      }
      const res: any = await api.post(`company/${companyId}/domains`, data);
      if (!res.error) {
        await fetchDomains();
      }
      return res;
    },
    [companyId, fetchDomains],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const deleteDomain = useCallback(
    async (id: number): Promise<MutationResult> => {
      if (!companyId) {
        return { error: true, message: 'No company selected' };
      }
      const res: any = await api.delete(`company/${companyId}/domains/${id}`);
      if (!res.error) {
        setDomains((prev) => prev.filter((d) => d.id !== id));
      }
      return res;
    },
    [companyId],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setPrimaryDomain = useCallback(
    async (id: number): Promise<MutationResult> => {
      if (!companyId) {
        return { error: true, message: 'No company selected' };
      }
      const res: any = await api.put(`company/${companyId}/domains/${id}/primary`, {});
      if (!res.error) {
        await fetchDomains();
      }
      return res;
    },
    [companyId, fetchDomains],
  );

  return {
    createDomain,
    deleteDomain,
    domains,
    fetchDomains,
    loading,
    setPrimary: setPrimaryDomain,
  };
}
